import React, { useState, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { adminService, CompanySettings, InvoiceTextTemplates, InvoiceTypeKey } from '../../services/adminService';
import './Settings.css';

// Invoice type labels for display
const INVOICE_TYPE_LABELS: Record<InvoiceTypeKey, string> = {
  'Deposit': 'Anzahlungsrechnung',
  'Final': 'Schlussrechnung',
  'ShortTerm': 'Kurzfristige Buchung',
  'Cancellation': 'Stornorechnung',
  'Credit': 'Gutschrift',
  'Correction': 'Korrekturrechnung'
};

interface BookingSettings {
  minParticipants: number;
  maxParticipants: number;
  depositPercentage: number;
  cancellationDeadline: number;
  autoConfirmBookings: boolean;
  requireEmailVerification: boolean;
  allowGuestBookings: boolean;
  bookingLeadTime: number;
  maxAdvanceBookingDays: number;
}

interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  emailSignature: string;
  enableEmailNotifications: boolean;
}

interface PaymentSettings {
  stripeEnabled: boolean;
  stripePublicKey: string;
  stripeSecretKey: string;
  paypalEnabled: boolean;
  paypalClientId: string;
  paypalSecretKey: string;
  klarnaEnabled: boolean;
  bankTransferEnabled: boolean;
  cashPaymentEnabled: boolean;
  invoicePaymentEnabled: boolean;
}

interface NotificationSettings {
  emailReminders: boolean;
  smsReminders: boolean;
  whatsappEnabled: boolean;
  reminderDaysBefore: number;
  sendBookingConfirmation: boolean;
  sendPaymentConfirmation: boolean;
  sendCancellationNotice: boolean;
  adminNotifications: boolean;
}

interface UserManagement {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Staff' | 'Support';
  permissions: string[];
  lastLogin: string;
  status: 'active' | 'inactive';
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('system');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Company Settings State (from Backend API)
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);

  // Booking Settings State
  const [bookingSettings, setBookingSettings] = useState<BookingSettings>({
    minParticipants: 30,
    maxParticipants: 200,
    depositPercentage: 20,
    cancellationDeadline: 14,
    autoConfirmBookings: false,
    requireEmailVerification: true,
    allowGuestBookings: false,
    bookingLeadTime: 7,
    maxAdvanceBookingDays: 365
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: 'noreply@frankenfloss.de',
    smtpPassword: '********',
    smtpSecure: true,
    fromEmail: 'noreply@frankenfloss.de',
    fromName: 'Frankenfloss',
    emailSignature: 'Mit freundlichen Grüßen\nIhr Frankenfloss Team',
    enableEmailNotifications: true
  });

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({
    stripeEnabled: true,
    stripePublicKey: 'pk_test_51234567890',
    stripeSecretKey: 'sk_test_********',
    paypalEnabled: true,
    paypalClientId: 'AYZ123456789',
    paypalSecretKey: '********',
    klarnaEnabled: false,
    bankTransferEnabled: true,
    cashPaymentEnabled: true,
    invoicePaymentEnabled: true
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailReminders: true,
    smsReminders: false,
    whatsappEnabled: true,
    reminderDaysBefore: 3,
    sendBookingConfirmation: true,
    sendPaymentConfirmation: true,
    sendCancellationNotice: true,
    adminNotifications: true
  });

  // Users Mock Data
  const [users, setUsers] = useState<UserManagement[]>([
    {
      id: '1',
      name: 'Admin User',
      email: 'admin@frankenfloss.de',
      role: 'Admin',
      permissions: ['all'],
      lastLogin: '2025-09-14T10:30:00',
      status: 'active'
    },
    {
      id: '2',
      name: 'Sarah Meyer',
      email: 'sarah@frankenfloss.de',
      role: 'Staff',
      permissions: ['bookings', 'customers', 'calendar'],
      lastLogin: '2025-09-14T09:15:00',
      status: 'active'
    }
  ]);

  // Load company settings from API on mount
  useEffect(() => {
    loadCompanySettings();
  }, []);

  const loadCompanySettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const settings = await adminService.getCompanySettings();
      setCompanySettings(settings);
      // Also update booking settings from company settings
      setBookingSettings(prev => ({
        ...prev,
        minParticipants: settings.minParticipants,
        maxParticipants: settings.maxParticipants,
        depositPercentage: Math.round(settings.depositPercentage * 100)
      }));
      // Update payment settings from company settings
      setPaymentSettings(prev => ({
        ...prev,
        stripeEnabled: settings.stripeEnabled ?? true,
        paypalEnabled: settings.payPalEnabled ?? true,
        klarnaEnabled: settings.klarnaEnabled ?? false,
        bankTransferEnabled: settings.bankTransferEnabled ?? true
      }));
    } catch (err: any) {
      console.error('Error loading company settings:', err);
      setError(err.response?.data?.error || 'Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setError(null);

    try {
      if (companySettings) {
        // Update company settings including booking and payment settings
        const updatedSettings = {
          ...companySettings,
          minParticipants: bookingSettings.minParticipants,
          maxParticipants: bookingSettings.maxParticipants,
          depositPercentage: bookingSettings.depositPercentage / 100,
          // Payment methods (API keys remain in appsettings.json for security)
          stripeEnabled: paymentSettings.stripeEnabled,
          payPalEnabled: paymentSettings.paypalEnabled,
          klarnaEnabled: paymentSettings.klarnaEnabled,
          bankTransferEnabled: paymentSettings.bankTransferEnabled
        };
        await adminService.updateCompanySettings(updatedSettings);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern der Einstellungen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Wirklich alle Einstellungen auf Standardwerte zurücksetzen?')) {
      loadCompanySettings();
    }
  };

  const handleTestEmail = () => {
    alert('Test-E-Mail wurde versendet an: ' + emailSettings.fromEmail);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await adminService.uploadCompanyLogo(file);
      if (companySettings) {
        setCompanySettings({
          ...companySettings,
          logoPath: result.logoPath
        });
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.error || 'Fehler beim Hochladen des Logos');
    }
  };

  const handleBackup = () => {
    const backupData = {
      companySettings,
      bookingSettings,
      emailSettings,
      paymentSettings,
      notificationSettings,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `frankenfloss-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const tabs = [
    { id: 'system', label: 'System', icon: '⚙️' },
    { id: 'booking', label: 'Buchungen', icon: '📅' },
    { id: 'invoices', label: 'Rechnungstexte', icon: '📄' },
    { id: 'email', label: 'E-Mail', icon: '📧' },
    { id: 'payment', label: 'Zahlungen', icon: '💳' },
    { id: 'notifications', label: 'Benachrichtigungen', icon: '🔔' },
    { id: 'users', label: 'Benutzer', icon: '👥' },
    { id: 'backup', label: 'Backup', icon: '💾' }
  ];

  if (isLoading) {
    return (
      <div className="settings-container loading">
        <div className="spinner">
          <ArrowPathIcon className="w-12 h-12 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        <h2>Einstellungen</h2>
        <p>Verwalten Sie alle Systemeinstellungen und Konfigurationen</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="success-message">
          ✅ Einstellungen erfolgreich gespeichert!
        </div>
      )}

      {/* Tab Navigation */}
      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="settings-content">
        {/* System Settings - Company Info */}
        {activeTab === 'system' && companySettings && (
          <div className="settings-section">
            <h3>Firmeneinstellungen</h3>
            <p className="section-description">Diese Daten erscheinen auf Rechnungen und im Briefkopf.</p>

            {/* Logo Upload */}
            <div className="form-group full-width logo-upload-section">
              <label>Firmenlogo</label>
              {companySettings.logoPath && (
                <div className="current-logo">
                  <img src={companySettings.logoPath} alt="Firmenlogo" style={{ maxHeight: '60px' }} />
                </div>
              )}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.svg"
                onChange={handleLogoUpload}
                className="file-input"
              />
              <small>PNG, JPG, GIF oder SVG (max. 5MB)</small>
            </div>

            <div className="form-grid">
              {/* Firma */}
              <div className="form-group">
                <label>Firmenname *</label>
                <input
                  type="text"
                  value={companySettings.companyName}
                  onChange={(e) => setCompanySettings({...companySettings, companyName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Straße</label>
                <input
                  type="text"
                  value={companySettings.companyStreet}
                  onChange={(e) => setCompanySettings({...companySettings, companyStreet: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>PLZ</label>
                <input
                  type="text"
                  value={companySettings.companyPostalCode}
                  onChange={(e) => setCompanySettings({...companySettings, companyPostalCode: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Ort</label>
                <input
                  type="text"
                  value={companySettings.companyCity}
                  onChange={(e) => setCompanySettings({...companySettings, companyCity: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="tel"
                  value={companySettings.companyPhone}
                  onChange={(e) => setCompanySettings({...companySettings, companyPhone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={companySettings.companyEmail}
                  onChange={(e) => setCompanySettings({...companySettings, companyEmail: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Webseite</label>
                <input
                  type="text"
                  value={companySettings.companyWebsite}
                  onChange={(e) => setCompanySettings({...companySettings, companyWebsite: e.target.value})}
                />
              </div>

              {/* Rechtlich */}
              <div className="form-group">
                <label>Geschäftsführer</label>
                <input
                  type="text"
                  value={companySettings.ceoName}
                  onChange={(e) => setCompanySettings({...companySettings, ceoName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Handelsregister (HRA)</label>
                <input
                  type="text"
                  value={companySettings.tradeRegisterNumber}
                  onChange={(e) => setCompanySettings({...companySettings, tradeRegisterNumber: e.target.value})}
                  placeholder="z.B. HRA 4791"
                />
              </div>
              <div className="form-group">
                <label>Registergericht</label>
                <input
                  type="text"
                  value={companySettings.tradeRegisterCourt}
                  onChange={(e) => setCompanySettings({...companySettings, tradeRegisterCourt: e.target.value})}
                  placeholder="z.B. Amtsgericht Coburg"
                />
              </div>
              <div className="form-group">
                <label>USt-IdNr.</label>
                <input
                  type="text"
                  value={companySettings.vatNumber}
                  onChange={(e) => setCompanySettings({...companySettings, vatNumber: e.target.value})}
                  placeholder="z.B. DE 286 992 717"
                />
              </div>

              {/* Komplementär (nur für GmbH & Co. KG) */}
              <div className="form-group">
                <label>Komplementär-GmbH</label>
                <input
                  type="text"
                  value={companySettings.complementaryCompany}
                  onChange={(e) => setCompanySettings({...companySettings, complementaryCompany: e.target.value})}
                  placeholder="z.B. Frankenfloß Verwaltungs GmbH"
                />
              </div>
              <div className="form-group">
                <label>HRB (Komplementär)</label>
                <input
                  type="text"
                  value={companySettings.complementaryRegisterNumber}
                  onChange={(e) => setCompanySettings({...companySettings, complementaryRegisterNumber: e.target.value})}
                  placeholder="z.B. HRB 5230"
                />
              </div>

              {/* Bank */}
              <div className="form-group">
                <label>Bankname</label>
                <input
                  type="text"
                  value={companySettings.bankName}
                  onChange={(e) => setCompanySettings({...companySettings, bankName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>IBAN</label>
                <input
                  type="text"
                  value={companySettings.bankIban}
                  onChange={(e) => setCompanySettings({...companySettings, bankIban: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>BIC</label>
                <input
                  type="text"
                  value={companySettings.bankBic}
                  onChange={(e) => setCompanySettings({...companySettings, bankBic: e.target.value})}
                />
              </div>

              {/* System */}
              <div className="form-group">
                <label>Währung</label>
                <select
                  value={companySettings.defaultCurrency}
                  onChange={(e) => setCompanySettings({...companySettings, defaultCurrency: e.target.value})}
                >
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
              <div className="form-group">
                <label>Zeitzone</label>
                <select
                  value={companySettings.timezone}
                  onChange={(e) => setCompanySettings({...companySettings, timezone: e.target.value})}
                >
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Booking Settings */}
        {activeTab === 'booking' && (
          <div className="settings-section">
            <h3>Buchungseinstellungen</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Min. Teilnehmer</label>
                <input type="number" value={bookingSettings.minParticipants}
                  onChange={(e) => setBookingSettings({...bookingSettings, minParticipants: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Max. Teilnehmer</label>
                <input type="number" value={bookingSettings.maxParticipants}
                  onChange={(e) => setBookingSettings({...bookingSettings, maxParticipants: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Anzahlung (%)</label>
                <input type="number" value={bookingSettings.depositPercentage}
                  onChange={(e) => setBookingSettings({...bookingSettings, depositPercentage: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Stornierungsfrist (Tage)</label>
                <input type="number" value={bookingSettings.cancellationDeadline}
                  onChange={(e) => setBookingSettings({...bookingSettings, cancellationDeadline: parseInt(e.target.value)})} />
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input type="checkbox" checked={bookingSettings.autoConfirmBookings}
                    onChange={(e) => setBookingSettings({...bookingSettings, autoConfirmBookings: e.target.checked})} />
                  Buchungen automatisch bestätigen
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Text Templates */}
        {activeTab === 'invoices' && companySettings && (
          <div className="settings-section">
            <h3>Rechnungstexte</h3>
            <p className="section-description">
              Definieren Sie Intro- und Outro-Texte für verschiedene Rechnungstypen.
              Diese erscheinen auf den PDF-Rechnungen.
            </p>

            {(Object.keys(INVOICE_TYPE_LABELS) as InvoiceTypeKey[]).map((typeKey) => (
              <div key={typeKey} className="invoice-text-section">
                <h4>{INVOICE_TYPE_LABELS[typeKey]}</h4>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Einleitungstext</label>
                    <textarea
                      rows={3}
                      placeholder={`Einleitungstext für ${INVOICE_TYPE_LABELS[typeKey]}...`}
                      value={companySettings.invoiceIntroTexts?.[typeKey] || ''}
                      onChange={(e) => setCompanySettings({
                        ...companySettings,
                        invoiceIntroTexts: {
                          ...companySettings.invoiceIntroTexts,
                          [typeKey]: e.target.value
                        }
                      })}
                    />
                    <small>Erscheint nach der Adresse, vor der Positionstabelle</small>
                  </div>
                  <div className="form-group full-width">
                    <label>Abschlusstext</label>
                    <textarea
                      rows={4}
                      placeholder={`Abschlusstext für ${INVOICE_TYPE_LABELS[typeKey]}...`}
                      value={companySettings.invoiceOutroTexts?.[typeKey] || ''}
                      onChange={(e) => setCompanySettings({
                        ...companySettings,
                        invoiceOutroTexts: {
                          ...companySettings.invoiceOutroTexts,
                          [typeKey]: e.target.value
                        }
                      })}
                    />
                    <small>Erscheint nach den Zahlungsinformationen</small>
                  </div>
                </div>
              </div>
            ))}

            <div className="invoice-text-help">
              <h4>Hinweise</h4>
              <ul>
                <li><strong>Anzahlungsrechnung:</strong> Wird bei Buchungsbestätigung erstellt (20% Anzahlung)</li>
                <li><strong>Schlussrechnung:</strong> Wird 25 Tage vor der Fahrt erstellt (Restbetrag)</li>
                <li><strong>Kurzfristige Buchung:</strong> Wenn weniger als 8 Wochen bis zur Fahrt</li>
                <li><strong>Stornorechnung:</strong> Bei Stornierung einer Buchung</li>
                <li><strong>Gutschrift:</strong> Bei Rückerstattungen</li>
                <li><strong>Korrekturrechnung:</strong> Bei nachträglichen Änderungen</li>
              </ul>
            </div>
          </div>
        )}

        {/* Email Settings */}
        {activeTab === 'email' && (
          <div className="settings-section">
            <h3>E-Mail-Einstellungen</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>SMTP-Host</label>
                <input type="text" value={emailSettings.smtpHost}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpHost: e.target.value})} />
              </div>
              <div className="form-group">
                <label>SMTP-Port</label>
                <input type="number" value={emailSettings.smtpPort}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpPort: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>SMTP-Benutzer</label>
                <input type="text" value={emailSettings.smtpUser}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpUser: e.target.value})} />
              </div>
              <div className="form-group">
                <label>SMTP-Passwort</label>
                <input type="password" value={emailSettings.smtpPassword}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpPassword: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Absender-E-Mail</label>
                <input type="email" value={emailSettings.fromEmail}
                  onChange={(e) => setEmailSettings({...emailSettings, fromEmail: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Absender-Name</label>
                <input type="text" value={emailSettings.fromName}
                  onChange={(e) => setEmailSettings({...emailSettings, fromName: e.target.value})} />
              </div>
            </div>
          </div>
        )}

        {/* Payment Settings */}
        {activeTab === 'payment' && (
          <div className="settings-section">
            <h3>Zahlungseinstellungen</h3>
            <p style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              🔒 <strong>Sicherheitshinweis:</strong> API-Keys und Zugangsdaten werden aus Sicherheitsgründen
              in der Server-Konfiguration (appsettings.json) verwaltet. Kontaktieren Sie Ihren Administrator für Änderungen.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px'
            }}>
              {/* Stripe */}
              <div style={{
                background: paymentSettings.stripeEnabled ? '#f0fdf4' : '#f9fafb',
                border: paymentSettings.stripeEnabled ? '2px solid #22c55e' : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>💳</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Stripe</h4>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Kreditkarten & Debitkarten</span>
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '26px',
                    flexShrink: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={paymentSettings.stripeEnabled}
                      onChange={(e) => setPaymentSettings({...paymentSettings, stripeEnabled: e.target.checked})}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: paymentSettings.stripeEnabled ? '#22c55e' : '#ccc',
                      transition: '0.3s',
                      borderRadius: '26px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '20px',
                        width: '20px',
                        left: paymentSettings.stripeEnabled ? '27px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: paymentSettings.stripeEnabled ? '#16a34a' : '#9ca3af',
                  margin: 0,
                  fontWeight: paymentSettings.stripeEnabled ? '500' : '400'
                }}>
                  {paymentSettings.stripeEnabled ? '✓ Aktiviert' : '○ Deaktiviert'}
                </p>
              </div>

              {/* PayPal */}
              <div style={{
                background: paymentSettings.paypalEnabled ? '#eff6ff' : '#f9fafb',
                border: paymentSettings.paypalEnabled ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>🅿️</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>PayPal</h4>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>PayPal-Konto & Guthaben</span>
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '26px',
                    flexShrink: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={paymentSettings.paypalEnabled}
                      onChange={(e) => setPaymentSettings({...paymentSettings, paypalEnabled: e.target.checked})}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: paymentSettings.paypalEnabled ? '#3b82f6' : '#ccc',
                      transition: '0.3s',
                      borderRadius: '26px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '20px',
                        width: '20px',
                        left: paymentSettings.paypalEnabled ? '27px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: paymentSettings.paypalEnabled ? '#2563eb' : '#9ca3af',
                  margin: 0,
                  fontWeight: paymentSettings.paypalEnabled ? '500' : '400'
                }}>
                  {paymentSettings.paypalEnabled ? '✓ Aktiviert' : '○ Deaktiviert'}
                </p>
              </div>

              {/* Klarna */}
              <div style={{
                background: paymentSettings.klarnaEnabled ? '#fdf4ff' : '#f9fafb',
                border: paymentSettings.klarnaEnabled ? '2px solid #d946ef' : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>🛒</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Klarna</h4>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Rechnung & Ratenzahlung</span>
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '26px',
                    flexShrink: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={paymentSettings.klarnaEnabled}
                      onChange={(e) => setPaymentSettings({...paymentSettings, klarnaEnabled: e.target.checked})}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: paymentSettings.klarnaEnabled ? '#d946ef' : '#ccc',
                      transition: '0.3s',
                      borderRadius: '26px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '20px',
                        width: '20px',
                        left: paymentSettings.klarnaEnabled ? '27px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: paymentSettings.klarnaEnabled ? '#c026d3' : '#9ca3af',
                  margin: 0,
                  fontWeight: paymentSettings.klarnaEnabled ? '500' : '400'
                }}>
                  {paymentSettings.klarnaEnabled ? '✓ Aktiviert' : '○ Deaktiviert'}
                </p>
              </div>

              {/* Überweisung */}
              <div style={{
                background: paymentSettings.bankTransferEnabled ? '#fefce8' : '#f9fafb',
                border: paymentSettings.bankTransferEnabled ? '2px solid #eab308' : '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>🏦</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Überweisung</h4>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Manuelle Banküberweisung</span>
                    </div>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '26px',
                    flexShrink: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={paymentSettings.bankTransferEnabled}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bankTransferEnabled: e.target.checked})}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: paymentSettings.bankTransferEnabled ? '#eab308' : '#ccc',
                      transition: '0.3s',
                      borderRadius: '26px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '20px',
                        width: '20px',
                        left: paymentSettings.bankTransferEnabled ? '27px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: paymentSettings.bankTransferEnabled ? '#ca8a04' : '#9ca3af',
                  margin: 0,
                  fontWeight: paymentSettings.bankTransferEnabled ? '500' : '400'
                }}>
                  {paymentSettings.bankTransferEnabled ? '✓ Aktiviert' : '○ Deaktiviert'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Settings */}
        {activeTab === 'notifications' && (
          <div className="settings-section">
            <h3>Benachrichtigungseinstellungen</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notificationSettings.emailReminders}
                    onChange={(e) => setNotificationSettings({...notificationSettings, emailReminders: e.target.checked})} />
                  E-Mail-Erinnerungen aktivieren
                </label>
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notificationSettings.sendBookingConfirmation}
                    onChange={(e) => setNotificationSettings({...notificationSettings, sendBookingConfirmation: e.target.checked})} />
                  Buchungsbestätigung senden
                </label>
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input type="checkbox" checked={notificationSettings.sendPaymentConfirmation}
                    onChange={(e) => setNotificationSettings({...notificationSettings, sendPaymentConfirmation: e.target.checked})} />
                  Zahlungsbestätigung senden
                </label>
              </div>
              <div className="form-group">
                <label>Erinnerung Tage vorher</label>
                <input type="number" value={notificationSettings.reminderDaysBefore}
                  onChange={(e) => setNotificationSettings({...notificationSettings, reminderDaysBefore: parseInt(e.target.value)})} />
              </div>
            </div>
          </div>
        )}

        {/* Users Management */}
        {activeTab === 'users' && (
          <div className="settings-section">
            <h3>Benutzerverwaltung</h3>
            <button className="btn btn-primary mb-20">➕ Neuer Benutzer</button>
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Rolle</th>
                    <th>Status</th>
                    <th>Letzter Login</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td><span className="role-badge">{user.role}</span></td>
                      <td><span className={`status-badge ${user.status}`}>{user.status}</span></td>
                      <td>{new Date(user.lastLogin).toLocaleString('de-DE')}</td>
                      <td>
                        <button className="btn-icon">✏️</button>
                        <button className="btn-icon">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Backup & Recovery */}
        {activeTab === 'backup' && (
          <div className="settings-section">
            <h3>Backup & Wiederherstellung</h3>
            <div className="backup-options">
              <div className="backup-card">
                <h4>📥 Daten exportieren</h4>
                <p>Erstellen Sie ein vollständiges Backup aller Systemdaten</p>
                <button onClick={handleBackup} className="btn btn-primary">
                  Backup erstellen
                </button>
              </div>
              <div className="backup-card">
                <h4>📤 Daten importieren</h4>
                <p>Stellen Sie Daten aus einem Backup wieder her</p>
                <input type="file" accept=".json" className="file-input" />
                <button className="btn btn-secondary">Import starten</button>
              </div>
              <div className="backup-card">
                <h4>🔄 Automatische Backups</h4>
                <p>Täglich um 03:00 Uhr</p>
                <label className="toggle-switch">
                  <input type="checkbox" checked={true} readOnly />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="settings-actions">
        <button onClick={handleResetSettings} className="btn btn-secondary">
          🔄 Zurücksetzen
        </button>
        {activeTab === 'email' && (
          <button onClick={handleTestEmail} className="btn btn-secondary">
            📧 Test-E-Mail senden
          </button>
        )}
        <button onClick={handleSaveSettings} className="btn btn-primary" disabled={isSaving}>
          {isSaving ? '⏳ Speichern...' : '💾 Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
